import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import type { SubmitSummary } from "@/lib/submitConfirm";

/**
 * FEAT-15 (Sheet #113): the confirm in front of every final submit a candidate
 * makes by clicking — how many questions are answered, how much time is left,
 * and that there is no way back. Presentational: the host screen owns the
 * submit and its pending / error state, exactly as with `ConfirmDialog`.
 *
 * `summary` is "loading" while the host is still counting and null when it
 * could not — the line is then left out, and Submit stays enabled either way:
 * the clock is running, so the count must never stand between a candidate and
 * the submit. `timeLeft` arrives already formatted by the host, with the same
 * formatter as its header clock, so the two numbers on screen agree; null
 * means this attempt shows no clock (EF-9 / FEAT-11) and the line is left out.
 *
 * No portal (`ConfirmDialog` has none) — it must stay inside the candidate
 * `translate="no"` subtree.
 *
 * Focus moves to "Not yet" on open and back to the opener on close
 * (`focusCancelOnOpen`): a keyboard candidate's first Tab must not land under
 * the overlay, and an Enter meant for the button behind must never submit.
 *
 * All wording here is a placeholder — the copy is Emilia's.
 */
export interface SubmitConfirmDialogProps {
  summary: SubmitSummary | "loading" | null;
  timeLeft: string | null;
  onConfirm: () => void;
  onCancel: () => void;
  isPending: boolean;
  error?: string | null;
}

/** The retryable failure line every host shows when the submit did not land. */
export const SUBMIT_FAILED_MESSAGE =
  "Could not submit the test. Please try again.";

// OPS-28 #2 / #30 — the section transitions' failure lines, shared by the
// question screen and the section review (R17, pre-ship 2026-10-18) so the
// rewrite lands once. ⚠ Placeholder copy, the wording is Emilia's (owed.md).
export const TIME_UP_CLOSE_FAILED_MESSAGE =
  "Time's up for this section, but we couldn't close it. Check your connection and retry.";
export const ADVANCE_FAILED_MESSAGE =
  "Couldn't move on to the next section. Check your connection and try again.";

export function SubmitConfirmDialog({
  summary,
  timeLeft,
  onConfirm,
  onCancel,
  isPending,
  error,
}: SubmitConfirmDialogProps) {
  const unanswered =
    summary && summary !== "loading" ? summary.total - summary.answered : 0;
  return (
    <ConfirmDialog
      title="Submit your test?"
      body={
        <>
          {summary === "loading" ? (
            <p>Checking your answers…</p>
          ) : (
            summary && (
              <p>
                You have answered{" "}
                <span className="font-semibold text-foreground">
                  {summary.answered} of {summary.total}
                </span>{" "}
                question{summary.total === 1 ? "" : "s"}.
                {unanswered > 0 && (
                  <>
                    {" "}
                    <span className="font-medium text-foreground">
                      {unanswered} {unanswered === 1 ? "has" : "have"} no
                      answer.
                    </span>
                  </>
                )}
              </p>
            )
          )}
          {timeLeft !== null && (
            <p>
              Time left:{" "}
              <span className="font-mono font-semibold tabular-nums text-foreground">
                {timeLeft}
              </span>
            </p>
          )}
          <p className="font-medium text-foreground">
            After you submit, you can&rsquo;t come back or change your answers.
          </p>
        </>
      }
      confirmLabel="Submit"
      pendingLabel="Submitting…"
      cancelLabel="Not yet"
      confirmVariant="default"
      onConfirm={onConfirm}
      onCancel={onCancel}
      isPending={isPending}
      error={error}
      focusCancelOnOpen
    />
  );
}
